import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ArrowRight, Check, CalendarClock, Lock } from 'lucide-react'
import { Panel, Button, Banner, useToast } from '../../ds/index'
import { Icon } from '../../components/Icon'
import { HandpickedTree } from '../../components/BrandMark'
import { LoadingBlock, ErrorBlock } from '../../components/Loading'
import { useApi } from '../../lib/useApi'
import { useSession } from '../../lib/session'
import { post, patch } from '../../lib/api'
import { money } from '../../lib/format'
import ClientProfile from './ClientProfile'
import { MethodStep, creditOf, methodName, type Method } from './ClientPaymentSetup'
import type { Billing } from './ClientBilling'
import type { Summary } from './ClientOverview'

export type OnboardingState = {
  done: boolean; welcomed: boolean; profile_complete: boolean; org_name: string; client_name: string; preview?: boolean
  step: 'welcome' | 'profile' | 'payment' | 'done'
  payment: { can_pay_online: boolean; monthly_on: boolean; monthly_amount_cents: number | null; minimum_cents: number }
}
type Stage = 'welcome' | 'profile' | 'payment' | 'ready'

/**
 * A new client's first sign-in, start to finish: the welcome, their business
 * profile, their monthly lead credit, then the portal. The server keeps where
 * they are, so a reload, a trip to Stripe or another device picks up the same
 * step.
 */
export default function Onboarding({ state, onFinished }: { state: OnboardingState; onFinished: () => void }) {
  const [params] = useSearchParams()
  const backFromStripe = params.get('card') || params.get('bank')
  const [stage, setStage] = useState<Stage>(() => (backFromStripe ? 'payment' : state.step === 'done' ? 'ready' : state.step))
  const [finished, setFinished] = useState<{ monthly: number | null } | null>(null)

  useEffect(() => { window.scrollTo(0, 0) }, [stage])

  if (stage === 'welcome') return <Welcome clientName={state.client_name} onNext={() => setStage('profile')} />
  return (
    <div className="ob-page">
      <header className="ob-bar">
        <span className="ob-brand"><HandpickedTree size={34} /><span className="hp-wordmark">Handpicked</span></span>
        <ol className="ob-steps" aria-label="Setup steps">
          {[{ k: 'profile', l: 'Your business' }, { k: 'payment', l: 'Monthly credit' }, { k: 'ready', l: 'Ready' }].map((x, i, all) => {
            const at = all.findIndex((y) => y.k === stage)
            return (
              <li key={x.k} className={i < at ? 'is-done' : i === at ? 'is-on' : ''} aria-current={i === at ? 'step' : undefined}>
                <span className="ob-step-dot">{i < at ? <Icon of={Check} size={12} /> : i + 1}</span><span>{x.l}</span>
              </li>
            )
          })}
        </ol>
      </header>
      <main className="ob-main">
        {stage === 'profile' && <ClientProfile onboarding={{ onContinue: () => setStage('payment') }} />}
        {stage === 'payment' && <PaymentStage state={state} onFinished={(monthly) => { setFinished({ monthly }); setStage('ready') }} />}
        {stage === 'ready' && <Ready orgName={state.org_name} monthly={finished?.monthly ?? null} onGo={onFinished} />}
      </main>
    </div>
  )
}

/**
 * Welcome to Handpicked: the tree grows in on a warm cream ground, leaves in
 * the brand's wine and gold drift down past it, the wordmark settles letter by
 * letter and a gold rule draws beneath. About three seconds, then the button.
 * With reduced motion it is the finished frame, still.
 */
function Welcome({ clientName, onNext }: { clientName: string; onNext: () => void }) {
  const { me } = useSession()
  const toast = useToast()
  const [busy, setBusy] = useState(false)
  const first = (me?.user.name || '').split(' ')[0]
  // Leaves: fixed per mount so a re-render never reshuffles them mid-fall.
  const leaves = useMemo(() => Array.from({ length: 16 }, (_, i) => ({
    left: 6 + ((i * 53) % 88), delay: 1.1 + ((i * 37) % 70) / 10, dur: 9 + ((i * 29) % 60) / 10,
    size: 10 + ((i * 17) % 12), sway: 30 + ((i * 41) % 50), gold: i % 5 === 2, turn: (i * 67) % 360,
  })), [])
  const word = 'HANDPICKED'
  const go = async () => {
    setBusy(true)
    try { await post('/api/client/onboarding/welcomed', {}); onNext() } catch (e) { toast((e as Error).message, 'neg'); setBusy(false) }
  }
  return (
    <div className="ob-welcome" role="main">
      <div className="ob-glow" aria-hidden="true" />
      <div className="ob-leaves" aria-hidden="true">
        {leaves.map((l, i) => (
          <span key={i} className={`ob-leaf${l.gold ? ' is-gold' : ''}`}
                style={{ left: `${l.left}%`, width: l.size, height: l.size * 1.7, animationDelay: `${l.delay}s, ${l.delay}s`, animationDuration: `${l.dur}s, ${l.dur / 3}s`,
                         ['--sway' as string]: `${l.sway}px`, ['--turn' as string]: `${l.turn}deg` }} />
        ))}
      </div>
      <div className="ob-welcome-inner">
        <div className="ob-tree-wrap">
          <svg className="ob-ring" viewBox="0 0 200 200" aria-hidden="true"><circle cx="100" cy="100" r="94" /></svg>
          <div className="ob-tree"><HandpickedTree size={176} /></div>
        </div>
        <p className="ob-kicker">Welcome to</p>
        <h1 className="ob-word" aria-label="Handpicked">
          {word.split('').map((ch, i) => <span key={i} style={{ animationDelay: `${1.35 + i * 0.07}s` }} aria-hidden="true">{ch}</span>)}
        </h1>
        <span className="ob-rule" aria-hidden="true" />
        <p className="ob-tagline">Premium leads. Real results.</p>
        <p className="ob-hello">{first ? `Hi ${first}. ` : ''}Let's get {clientName || 'your business'} ready for its first leads. Two quick steps: tell us about your business, then choose your monthly lead credit.</p>
        <div className="ob-cta">
          <Button onClick={go} disabled={busy}>{busy ? 'One moment' : 'Get started'} <Icon of={ArrowRight} size={16} /></Button>
        </div>
      </div>
    </div>
  )
}

type PaySub = 'amount' | 'method' | 'confirm'

/** The monthly lead credit: how much (at least the minimum), how they pay, then turn it on. */
function PaymentStage({ state, onFinished }: { state: OnboardingState; onFinished: (monthly: number | null) => void }) {
  const b = useApi<Billing>('/api/client/billing')
  const s = useApi<Summary>('/api/client/summary')
  const toast = useToast()
  const nav = useNavigate()
  const [params] = useSearchParams()
  const [returned] = useState(() => ({ card: params.get('card'), bank: params.get('bank') }))
  const [sub, setSub] = useState<PaySub | null>(null)
  const [method, setMethod] = useState<Method>(returned.bank === 'saved' ? 'bank' : 'card')
  const [waiting, setWaiting] = useState(returned.card === 'saved' || returned.bank === 'saved')
  const [busy, setBusy] = useState(false)

  useEffect(() => { if (returned.card || returned.bank) nav('/portal/welcome', { replace: true }) }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Back from Stripe: the method is saved when Stripe's message lands, usually seconds. Look until it shows.
  useEffect(() => {
    if (!waiting || !b.data) return
    if (method === 'bank' ? b.data.bank : b.data.card) { setWaiting(false); setSub('confirm'); return }
    const t = setTimeout(b.reload, 2000)
    const stop = setTimeout(() => setWaiting(false), 30000)
    return () => { clearTimeout(t); clearTimeout(stop) }
  }, [waiting, b.data]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (sub || !b.data || waiting) return
    setSub(returned.card || returned.bank ? 'method' : 'amount')
  }, [b.data, waiting]) // eslint-disable-line react-hooks/exhaustive-deps

  const finish = async (later: boolean, monthly: number | null) => {
    setBusy(true)
    try { await post('/api/client/onboarding/finish', { later }); onFinished(monthly) } catch (e) { toast((e as Error).message, 'neg'); setBusy(false) }
  }

  if (b.error) return <ErrorBlock error={b.error} />
  if (!b.data || !s.data || !sub) return <LoadingBlock />
  const d = b.data
  const bps = s.data.wallet.discount_bps

  if (!state.payment.can_pay_online || !d.can_pay_online) {
    return (
      <>
        <div className="ob-head"><span className="hero-kicker">Step 2 of 2</span><h1>Your monthly lead credit</h1></div>
        <Panel>
          <p style={{ marginTop: 0 }}>Leads are paid from credit you add each month, at least {money(state.payment.minimum_cents)}. {d.org.name} will set up how you pay with you directly.</p>
          <div className="setup-actions"><Button onClick={() => finish(true, null)} disabled={busy}>{busy ? 'One moment' : 'Continue to your portal'} <Icon of={ArrowRight} size={15} /></Button></div>
        </Panel>
      </>
    )
  }

  return (
    <>
      <div className="ob-head">
        <span className="hero-kicker">Step 2 of 2</span>
        <h1>Your monthly lead credit</h1>
        <p className="muted">Leads are paid from credit. Choose how much to add each month, at least {money(d.org.monthly_minimum_cents)}. It goes to {d.org.name} through Stripe, and you can change or stop it any time on Billing.</p>
      </div>
      {waiting && <Panel><div className="setup-wait"><span className="spinner" aria-hidden="true" /><div><strong>Saving your {method === 'bank' ? 'bank account' : 'card'}</strong><span className="muted">Stripe is confirming it. This takes a few seconds.</span></div></div></Panel>}
      {(returned.card === 'cancelled' || returned.bank === 'cancelled') && sub === 'method' && <Banner tone="warn" title="Nothing saved">You closed the form. Nothing was charged.</Banner>}
      {!waiting && sub === 'amount' && <AmountPick billing={d} bps={bps} onPicked={() => { b.reload(); setSub('method') }} />}
      {!waiting && sub === 'method' && (
        <MethodStep billing={d} method={method} returnTo="welcome" onAdding={setMethod} onUse={(m) => { setMethod(m); setSub('confirm') }} />
      )}
      {!waiting && sub === 'confirm' && (
        <Confirm billing={d} bps={bps} method={method} busy={busy} onBack={() => setSub('amount')}
                 onOn={async () => {
                   setBusy(true)
                   try { await patch('/api/client/autopay', { on: true, method }); await finish(false, d.monthly_amount_cents) }
                   catch (e) { toast((e as Error).message, 'neg'); setBusy(false) }
                 }} />
      )}
      {!waiting && (
        <p className="ob-later">
          <button type="button" className="link-button" disabled={busy} onClick={() => finish(true, null)}>I'll set up payment later</button>
          <span className="muted"> Leads only reach you once you have credit.</span>
        </p>
      )}
    </>
  )
}

function AmountPick({ billing, bps, onPicked }: { billing: Billing; bps: number; onPicked: () => void }) {
  const toast = useToast()
  const min = billing.org.monthly_minimum_cents
  const choices = [...new Set([min, min * 2, min * 3, min * 5].filter((c) => c >= 1000))]
  const [cents, setCents] = useState(Math.max(min, billing.monthly_amount_cents || min))
  const [custom, setCustom] = useState('')
  const [busy, setBusy] = useState(false)
  const amount = custom ? Math.round(Number(custom) * 100) : cents
  const valid = Number.isInteger(amount) && amount >= min
  const save = async () => {
    if (!valid) return toast(`The monthly amount is at least ${money(min)}`, 'neg')
    setBusy(true)
    try { await patch('/api/client/payment-settings', { monthly_amount_cents: amount }); onPicked() } catch (e) { toast((e as Error).message, 'neg') } finally { setBusy(false) }
  }
  return (
    <Panel>
      <div className="setup-step-head">
        <span className="setup-step-icon"><Icon of={CalendarClock} size={20} /></span>
        <div><h2>How much each month?</h2><p className="muted">{bps ? `Credit is ${bps / 100}% off: every ${money(10000)} you add buys ${money(creditOf(10000, bps))} of leads. ` : ''}Unused credit carries over.</p></div>
      </div>
      <div className="amount-picks" role="radiogroup" aria-label="Monthly amount">
        {choices.map((c) => (
          <button key={c} type="button" role="radio" aria-checked={!custom && cents === c} aria-label={`${money(c)} a month, ${money(creditOf(c, bps))} of credit`} className="amount-pick" onClick={() => { setCents(c); setCustom('') }}>
            <strong>{money(c)}</strong><span>{money(creditOf(c, bps))} of credit</span>{c === min && <em>Minimum</em>}
          </button>
        ))}
        <label className={`amount-pick is-custom${custom ? ' is-on' : ''}`}>
          <span className="muted">Other</span>
          <span className="amount-input">$<input inputMode="decimal" value={custom} placeholder={(min / 100).toString()} onChange={(e) => setCustom(e.target.value.replace(/[^0-9.]/g, ''))} aria-label="Other monthly amount, dollars" /></span>
        </label>
      </div>
      <div className="setup-actions"><Button onClick={save} disabled={busy || !valid}>{busy ? 'Saving' : `Continue with ${valid ? money(amount) : ''} a month`} <Icon of={ArrowRight} size={15} /></Button></div>
    </Panel>
  )
}

function Confirm({ billing, bps, method, busy, onBack, onOn }: { billing: Billing; bps: number; method: Method; busy: boolean; onBack: () => void; onOn: () => void }) {
  const amount = billing.monthly_amount_cents
  return (
    <Panel>
      <div className="setup-step-head">
        <span className="setup-step-icon"><Icon of={Lock} size={20} /></span>
        <div><h2>Turn on your monthly credit</h2><p className="muted">You can change the amount or stop it any time on Billing.</p></div>
      </div>
      <div className="setup-summary">
        <strong>{money(amount)} today</strong>, then {money(amount)} on the 1st of every month, from your {methodName(billing, method)}. Each payment adds {money(creditOf(amount, bps))} of lead credit{method === 'bank' ? ', once the payment clears (3 to 5 business days)' : ''}.
      </div>
      <div className="setup-actions">
        <Button onClick={onOn} disabled={busy}>{busy ? 'Turning on' : `Turn on ${money(amount)} a month`}</Button>
        <Button variant="ghost" onClick={onBack} disabled={busy}>Change the amount</Button>
      </div>
    </Panel>
  )
}

/** Done: a check that draws itself, what is set up, and the way into the portal. */
function Ready({ orgName, monthly, onGo }: { orgName: string; monthly: number | null; onGo: () => void }) {
  return (
    <div className="ob-ready">
      <svg className="ob-check" viewBox="0 0 72 72" aria-hidden="true"><circle cx="36" cy="36" r="33" /><path d="M22 37.5l9.5 9.5L51 27" /></svg>
      <h1>You're all set</h1>
      <p className="muted">Your profile is in{monthly ? ` and ${money(monthly)} a month of lead credit is on` : ''}. {orgName}'s reps will start recommending you in the groups they watch. Every lead shows up in your portal, and we email you each time.</p>
      <Button onClick={onGo}>Go to your portal <Icon of={ArrowRight} size={16} /></Button>
    </div>
  )
}
